// Maps a backend fulfillment `error_code` to an operator-facing i18n key.
// Only codes with a dedicated, actionable message live here; anything else
// falls back to the raw server message (see resolveGenerateLabelErrorMessage).
export const ERROR_CODE_MESSAGES = {
  ORDER_DIFAL_INVOICE_EXISTS: 'generate_shipment.error_codes.difal_invoice_exists',
  ORDER_STORE_EDIT_RESTRICTION: 'generate_shipment.error_codes.store_edit_restriction',
  ORDER_NOT_MODIFIABLE: 'generate_shipment.error_codes.order_not_modifiable',
  ORDER_LOCKED_BY_PROCESS: 'generate_shipment.error_codes.order_locked_by_process',
  ORDER_ALREADY_PACKED: 'generate_shipment.error_codes.order_already_packed',
  ORDER_NOT_AUTHORIZED: 'generate_shipment.error_codes.order_not_authorized',
  INVOICE_GENERATION_IN_PROGRESS: 'generate_shipment.error_codes.invoice_generation_in_progress',
  // BUGS-6299: the coverage-retry terminal (CUBBO-1635) renders
  // { error_code: 'CARRIER_NO_COVERAGE', reason, message } with no `code`
  // and no `error`. Without a mapping here AND a `message` fallback below,
  // the operator UI surfaced `undefined` — no error shown, screen stuck.
  CARRIER_NO_COVERAGE: 'generate_shipment.error_codes.carrier_no_coverage',
}

// Server-side packing gates (BUGS-8257). They arrive on `code`, not `error_code`,
// and carry `missing` as { sku: qty }. Defense in depth: the footer already refuses
// while pieces or open boxes remain, so these surface when the browser drifted.
export const PACKING_GATE_MESSAGES = {
  packing_incomplete: 'generate_shipment.error_codes.packing_incomplete',
  boxes_still_open: 'generate_shipment.error_codes.boxes_still_open',
}

// "SKU-A (4), SKU-B (1)". Empty when the backend sent no detail.
export const formatMissingPieces = missing => {
  if (!missing || typeof missing !== 'object') return ''
  return Object.entries(missing)
    .filter(([, quantity]) => Number(quantity) > 0)
    .map(([sku, quantity]) => `${sku} (${quantity})`)
    .join(', ')
}

// Raw server message fallback for a failed request: prefer the legacy `error`
// field, then the newer `message` field, then the JS error's own message.
export const getBackendErrorMessage = err => err?.response?.data?.error || err?.response?.data?.message || err?.message || ''

// Resolve the operator-facing message for a failed generate_label request.
// A known `error_code` maps to a translated, actionable message; otherwise we
// fall back to whatever message the server provided. Reading `message` (not
// just `error`) is the core BUGS-6299 fix: the coverage-retry terminal carries
// only `message`, so the previous `data.error`-only fallback yielded undefined.
export const resolveGenerateLabelErrorMessage = (err, translate) => {
  const gateCode = err?.response?.data?.code
  if (gateCode && PACKING_GATE_MESSAGES[gateCode]) {
    const pieces = formatMissingPieces(err?.response?.data?.missing)
    if (!pieces) return translate(PACKING_GATE_MESSAGES[gateCode])
    // Interpolated here: both call sites pass a single-argument translator, so
    // i18n options would be dropped and the operator would read "{{pieces}}".
    //
    // The replacement is a FUNCTION, never the string itself. String.prototype.replace
    // gives a string replacement PATTERN semantics, and `pieces` is built from
    // server-supplied SKUs: a SKU carrying `$&`, '$`', `$'`, `$$` or `$1` would be
    // reinterpreted instead of inserted. "A$&B (2)" rendered as "A{{pieces}}B (2)",
    // and "SKU-$`X (1)" spliced the whole preceding sentence into itself — in the one
    // message whose entire job is to name the missing SKUs. A function's return value
    // is inserted verbatim. Do NOT "simplify" this back to .replace('{{pieces}}', pieces).
    const detail = translate('generate_shipment.error_codes.missing_pieces_detail').replace('{{pieces}}', () => pieces)
    return `${translate(PACKING_GATE_MESSAGES[gateCode])} ${detail}`
  }

  const code = err?.response?.data?.error_code
  if (code && ERROR_CODE_MESSAGES[code]) {
    return translate(ERROR_CODE_MESSAGES[code])
  }
  return getBackendErrorMessage(err)
}
